<template>
    <div class="pb-5">
        <div class="text-12 font-medium text-a-black-484848 mb-1.5">
            {{ inputLbl.date.name }}
            <template v-if="!!inputLbl.date.required">
                <span class="text-red-500">*</span>
            </template>
        </div>
        <div class="grid grid-cols-2 gap-3">
            <div>
                <div class="px-4 py-1 border rounded-full relative h-10"
                    :class="[isError ? 'border-red-500' : 'border-a-black-555555']">
                    <Datepicker ref="elemDtPicker" class="w-full h-full" v-model="pickedDate" :minDate="lowerLimit"
                        :maxDate="upperLimit" :locale="dtLocale" :formatLocale="localeDate" :format="'dd/MM/yyyy'"
                        :disabled="isDisabled" @update:modelValue="onSelectDate()" :autoApply="true" :clearable="false"
                        :transitions="false" :enableTimePicker="false" :cancelText="lbl.button.cancel"
                        :selectText="lbl.button.ok">
                        <template #dp-input="{ value }">
                            <input type="text" readonly
                                class="outline-none appearance-none border-none font-medium text-a-black-484848 text-12 pr-6 w-full h-7.5"
                                :class="[isError ? ' placeholder-red-500' : 'placeholder-a-gray-d0d0ce']"
                                :placeholder="isError ? getErrorMsgDate : inputLbl.date.placeholder" :value="value" />
                        </template>
                    </Datepicker>
                    <div class="absolute right-3 top-2.5">
                        <img src="@/assets/icon/ic-calendar.svg" alt="calendar" />
                    </div>
                </div>
            </div>
            <div>
                <div class="px-4 py-1 border rounded-full relative h-10"
                    :class="[isError ? 'border border-red-500' : 'border-a-black-555555']">
                    <Datepicker ref="elemTimePicker" class="w-full h-full" v-model="pickedTime" :locale="dtLocale"
                        :formatLocale="localeDate" :is24="true" :disabled="isDisabled" timePicker :clearable="false"
                        @update:modelValue="onSelectTime" :transitions="false" :cancelText="lbl.button.cancel"
                        :selectText="lbl.button.ok" :minTime="minTime" :maxTime="maxTime">
                        <template #dp-input="{ value }">
                            <input type="text" readonly
                                class="outline-none appearance-none border-none font-medium text-a-black-484848 text-12 pr-6 w-full h-7.5"
                                :class="[isError ? ' placeholder-red-500' : 'placeholder-a-gray-d0d0ce']"
                                :placeholder="isError ? getErrorMsgTime : inputLbl.time.placeholder" :value="value" />
                        </template>
                    </Datepicker>

                    <div class="absolute h-full right-3 top-2.5">
                        <img src="@/assets/icon/ic-clock.svg" alt="clock" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import Util from '@/helper/Utility.js';
import DatetimeHelper from '@/helper/DatetimeHelper';
//lib
import Datepicker from '@vuepic/vue-datepicker' // https://vue3datepicker.com/installation/
import { th, enUS } from 'date-fns/locale'
//mixin
import LocaleMixin from '@/mixin/LocaleMixin';
import { DateTime } from 'luxon';

/**
 * @property {String} v-model:inputValue (required) bind value
 * @property {Date} lowerLimit (optional) date before can not select.
 * @property {Date} upperLimit (optional) date after can not select.
 * @property {Boolean} isDisabled (optional) disable input
 * @property {String} dateFormat (optional) date format
 * @property {Object} inputLbl (required) label for input
 * @property {Object} errorInfo (required) Validate result. example : { isValid: true, msg: '',}
 * @property {Boolean} isShowError (optional) Show/Hide Error label.
 * @property {Boolean} isSubmitted (optional) Show/Hide Error after save.
 */
export default {
    name: 'DateTimePicker',
    mixins: [LocaleMixin],
    components: {
        Datepicker,
    },
    props: {
        inputValue: { type: [String, Date, Number], default: '' },
        time: { type: [String, Object] },

        lowerLimit: { type: Date, default: null },
        upperLimit: { type: Date, default: null },
        isDisabled: { type: Boolean, default: false },
        dateFormat: { type: String, default: 'dd/MM/yyyy' },

        inputLbl: { type: Object, required: true },
        errorInfo: { type: Object, default: null },
        isShowError: { type: Boolean, default: false },
        isSubmitted: { type: Boolean, default: false },
    },
    emits: ["update:inputValue", "update:time", "onDateChange"],
    data: function () {
        return {
            pickedDate: null,
            pickedTime: null,
            currentDate: new Date(),
            localeDate: null,
            minTime: { hours: 8, minutes: 0, seconds: 0 },
            maxTime: { hours: 16, minutes: 59, seconds: 59 }
        };
    },
    created: function () {
        if (this.localeCode == 1033) {
            this.localeDate = enUS;
        } else {
            this.localeDate = th;
        }
        this.initInput();
    },
    mounted: function () {
        this.validateInput();

        this.$refs.elemTimePicker.selectDate(this.pickedDate)
    },
    computed: {
        isError: function () {
            return (this.isShowError && this.isSubmitted && !this.errorInfo.isValid);
        },
        getErrorMsgDate: function () {
            if (this.isShowError && this.isSubmitted && !this.errorInfo.isValid) {
                return this.inputLbl.date.required
            }
            return '';
        },
        getErrorMsgTime: function () {
            if (this.isShowError && this.isSubmitted && !this.errorInfo.isValid) {
                return this.inputLbl.time.required
            }
            return '';
        },
        dtLocale: function () {
            if (this.localeCode == 1033) {
                return "en-US";
            } else {
                return "th-TH";
            }
        }
    },
    methods: {
        initInput: function () {
            if (Util.isEmptyStr(this.inputValue)) {
                
                return;
            }

            let date = null;
            let checkDate = this.inputValue.toString();

            if (this.inputValue instanceof Date) {
                date = this.inputValue;
            } else if (checkDate.match(/^-{?}[0-9]+$/)) {
                date = DatetimeHelper.unixTimestampToDate(this.inputValue);
            } else {
                date = DatetimeHelper.stringToDate(this.inputValue, 'YYYY-MM-DD');
            }

            this.pickedDate = date;

            // time handler
            this.pickedTime = this.time
        },
        onSelectDate: function () {
            this.validateInput();
            let inputDate = this.pickedDate;
            let timestamp = DatetimeHelper.convertDateToTimeStampUnix(inputDate);
            let formated = DatetimeHelper.dateIsoFormat(inputDate);
            let value = {
                date: inputDate,
                timestamp: timestamp,
                formated: formated,
            };

            this.$emit('update:inputValue', inputDate);
            this.$emit('onDateChange', value);

            // update pick time
            this.$refs.elemTimePicker.selectDate(inputDate)

            // update condition
            this.handleDateChange(inputDate)
        },
        /**
         * handle time when date changes
         * @param {Date} inputDate 
         */
        handleDateChange: function (inputDate) {
            let currentDt = DateTime.now()
            let isCurrentDate = currentDt.hasSame(DateTime.fromJSDate(inputDate), "day")
            if (isCurrentDate) {
                // today case
                if (currentDt.hour < 16) {
                    let appointTime = currentDt.hour < 8 ? currentDt.startOf("day").plus({ hours: 9, minutes: 0 }) : currentDt.plus({ hours: 1 })
                    this.minTime = { hours: appointTime.hour, minutes: appointTime.minute }
                    this.pickedTime = { hours: appointTime.hour, minutes: appointTime.minute }
                    this.$emit('update:time', this.pickedTime);
                } else {
                    // reset to default with tomorrow
                    // console.log('should reset to tomorrow')
                    this.resetTimeConfig(true)
                }
            } else {
                // reset only time to 9:00
                this.resetTimeConfig()
            }

            this.validateInput()
        },
        resetTimeConfig: function (setTomorrow = false) {
            this.minTime = { hours: 8, minutes: 0, seconds: 0 }
            this.maxTime = { hours: 16, minutes: 59, seconds: 59 }

            if (setTomorrow === true) {
                let tomorrow = DateTime.now().startOf("day").plus({ days: 1 })
                this.pickedDate = tomorrow.toJSDate()
                this.$refs.elemTimePicker.selectDate(this.pickedDate)
                this.$emit("update:inputValue", this.pickedDate)
            }

            this.pickedTime = { hours: 9, minutes: 0 }
            this.$emit("update:time", this.pickedTime)
        },
        onSelectTime: function () {
            this.validateInput()
            // console.log(this.pickedTime)
            if (this.pickedTime != null) {
                this.$emit("update:time", this.pickedTime)
            }
        },

        validateInput: function () {
            let dateValid = false
            let timeValid = false
            if (this.pickedDate != null) {
                dateValid = true;
            } else {
                dateValid = false;
            }

            if (this.pickedTime != null && this.pickedTime.hours >= 8 && this.pickedTime.hours < 17) {
                timeValid = true
            } else {
                timeValid = false
            }
            // console.log(dateValid, timeValid)
            if (dateValid && timeValid) {
                this.errorInfo.setValid(true);
                return true
            } else {
                this.errorInfo.setValid(false);
                
                return false
            }
        },
        openDtPicker: function () {
            this.$refs.elemDtPicker.openMenu()
        },
        openTimePicker: function () {
            this.$refs.elemTimePicker.openMenu()
        }
    },
}
</script>

